import React, { useState, useEffect } from 'react';
import { InvestigationTemplate } from '../types';
import { investigationService } from '../investigationService';

export const InvestigationTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<InvestigationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<InvestigationTemplate | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await investigationService.getTemplates();
      setTemplates(data || []);
      if (data && data.length > 0) {
        setSelectedTemplate(data[0]);
      }
    } catch (err: any) {
      console.error('Failed to load investigation templates:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Overview Card */}
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '20px',
        }}
      >
        <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#0f172a', fontWeight: 700 }}>
          Investigation Methodology Frameworks & Standardized Templates
        </h3>
        <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
          Luminix adheres to ICAO Annex 13, EASA Part-IS / Reg (EU) 376/2014, and Just Culture principles.
          All investigations must execute standardized evidence collection, apply structured causal root analysis
          (5-Why, Ishikawa 6M, HFACS), and issue actionable safety recommendations.
        </p>
      </div>

      {/* Grid of Templates & Methodologies */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '20px' }}>
        {/* Template List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
            Standard Dossier Templates ({templates.length})
          </div>

          {!loading && templates.length === 0 ? (
            <div style={{ padding: '20px', color: '#94a3b8', fontSize: '13px' }}>No templates found</div>
          ) : (
            templates.map((tpl) => (
              <div
                key={tpl.id}
                onClick={() => setSelectedTemplate(tpl)}
                style={{
                  background: selectedTemplate?.id === tpl.id ? '#eff6ff' : '#ffffff',
                  border: `1px solid ${selectedTemplate?.id === tpl.id ? '#3b82f6' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  padding: '14px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a' }}>
                    {tpl.templateName}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: 600,
                      background: '#e0f2fe',
                      color: '#0369a1',
                    }}
                  >
                    {tpl.investigationType}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {Array.isArray(tpl.checklistJson) ? `${tpl.checklistJson.length} Checkpoints` : 'Custom Checkpoints'}
                </div>
              </div>
            ))
          )}

          {/* Standard Methodologies Reference Box */}
          <div
            style={{
              marginTop: '16px',
              padding: '16px',
              background: '#f1f5f9',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', marginBottom: '8px' }}>
              Engineered Analysis Toolkits
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#475569', lineHeight: 1.6 }}>
              <li><strong>5-Why Root-Cause Engine:</strong> Minimum 3 iterative layers with validation.</li>
              <li><strong>Ishikawa 6M Matrix:</strong> Man, Machine, Method, Material, Measurement, Milieu.</li>
              <li><strong>HFACS v7 Taxonomy:</strong> Unsafe Acts, Preconditions, Supervision, Organizational.</li>
              <li><strong>SHA-256 Chain of Custody:</strong> Tamper-evident evidence hashing.</li>
              <li><strong>Just Culture Vault:</strong> AES/Base64 protected witness interviews.</li>
            </ul>
          </div>
        </div>

        {/* Selected Template Details */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '20px',
          }}
        >
          {selectedTemplate ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>{selectedTemplate.templateName}</h4>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Type: {selectedTemplate.investigationType}</span>
                </div>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    background: '#dcfce7',
                    color: '#15803d',
                    padding: '3px 10px',
                    borderRadius: '12px',
                  }}
                >
                  SYSTEM STANDARD
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Standard Investigation Checkpoints & Milestones
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Array.isArray(selectedTemplate.checklistJson) && selectedTemplate.checklistJson.length > 0 ? (
                    selectedTemplate.checklistJson.map((c, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 14px',
                          background: '#f8fafc',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0',
                          fontSize: '13px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#475569' }}>
                            {idx + 1}
                          </span>
                          <span style={{ color: '#1e293b', fontWeight: 500 }}>{c.item}</span>
                        </div>
                        {c.mandatory && (
                          <span style={{ fontSize: '11px', fontWeight: 600, background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: '4px' }}>
                            Mandatory
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>No predefined checklist items configured.</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '40px' }}>
              Select a template to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
